// src/hooks/useSavedReports.ts
// User-saved report variants (via Save As).

import { useCallback, useState } from 'react';
import type { ReportCustomization, SavedReport } from '../pages/Accounting/Reports/AccountList/types';

interface UseSavedReportsReturn {
  data: SavedReport[];
  loading: boolean;
  error: Error | null;
  saveReport: (input: { name: string; shareWith: 'none' | 'everyone' | string; group: string | null; customization: ReportCustomization }) => SavedReport;
  loadReport: (id: string) => SavedReport | undefined;
  deleteReport: (id: string) => void;
  refetch: () => void;
}

const STORAGE_KEY = 'novala:saved-reports:account-list';

function readFromStorage(): SavedReport[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as SavedReport[];
  } catch (e) {
    // ignore
  }
  return [];
}

function writeToStorage(items: SavedReport[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // ignore
  }
}

export function useSavedReports(): UseSavedReportsReturn {
  const [data, setData] = useState<SavedReport[]>(function () { return readFromStorage(); });
  const [loading] = useState(false);
  const [error] = useState<Error | null>(null);

  const saveReport = useCallback(function (input: { name: string; shareWith: 'none' | 'everyone' | string; group: string | null; customization: ReportCustomization }) {
    const next: SavedReport = {
      id: 'sr_' + Date.now().toString(36),
      name: input.name,
      shareWith: input.shareWith,
      group: input.group,
      customization: input.customization,
      createdAt: new Date().toISOString(),
    };
    setData(function (prev) {
      const out = [next, ...prev];
      writeToStorage(out);
      return out;
    });
    return next;
  }, []);

  const loadReport = useCallback(function (id: string) {
    return data.find(function (r) { return r.id === id; });
  }, [data]);

  const deleteReport = useCallback(function (id: string) {
    setData(function (prev) {
      const out = prev.filter(function (r) { return r.id !== id; });
      writeToStorage(out);
      return out;
    });
  }, []);

  const refetch = useCallback(function () {
    setData(readFromStorage());
  }, []);

  return { data, loading, error, saveReport, loadReport, deleteReport, refetch };
}